import { Badge, Button, Table } from "react-bootstrap";
import { ClusterType, CustomerCluster } from "../../types/CustomerCluster";
import { StripeCharge } from "../../types/StripeCharge";
import { i18nAmount } from "../../lib/i18n";
import "./ClusterDetailSidebar.scss";

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function clusterTypeBadgeVariant(type: ClusterType): string {
  switch (type) {
    case "education":
      return "success";
    case "corporate":
      return "primary";
    case "individual":
      return "secondary";
  }
}

interface ClusterDetailSidebarProps {
  cluster: CustomerCluster;
  charges: StripeCharge[];
  onClose: () => void;
}

const ClusterDetailSidebar: React.FC<ClusterDetailSidebarProps> = ({
  cluster,
  charges,
  onClose,
}) => {
  return (
    <>
      <div className="cluster-sidebar-overlay" onClick={onClose} />
      <div className="cluster-sidebar">
        <div className="cluster-sidebar-header">
          <Button variant="link" className="close-btn" onClick={onClose}>
            &times;
          </Button>
        </div>

        <div className="cluster-sidebar-overview">
          <div className="overview-domain">
            {cluster.domain}{" "}
            <Badge bg={clusterTypeBadgeVariant(cluster.clusterType)}>
              {cluster.clusterType}
            </Badge>
          </div>
          <div className="overview-stats">
            <span>
              {i18nAmount(cluster.customerEmails.length, {
                singular: "customer",
                plural: "customers",
              })}
            </span>
            <span>
              {i18nAmount(cluster.chargeCount, {
                singular: "charge",
                plural: "charges",
              })}
            </span>
            {Object.entries(cluster.totalAmountByCurrency).map(
              ([currency, amount]) => (
                <span key={currency}>{formatCurrency(amount, currency)}</span>
              ),
            )}
          </div>
        </div>

        <div className="cluster-sidebar-table">
          <Table striped hover size="sm">
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer Email</th>
                <th>Customer Name</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((charge: StripeCharge) => (
                <tr key={charge.id}>
                  <td>{formatDate(charge.created)}</td>
                  <td>{charge.customerEmail ?? "—"}</td>
                  <td>{charge.customerName ?? "—"}</td>
                  <td>{formatCurrency(charge.amount, charge.currency)}</td>
                  <td>
                    <Badge
                      bg={charge.status === "succeeded" ? "success" : "warning"}
                    >
                      {charge.status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {charges.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted text-center">
                    No charges found for this cluster.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </>
  );
};

export default ClusterDetailSidebar;
